import { useContext, useEffect } from "react";
import Footer from "components/footer/Footer";
import Header from "components/header/Header";
import Legal from "components/legal/Legal";
import MessageCenter from "components/message-center/MessageCenter";
import TravelAgentHeader from "components/travel-agent-header/TravelAgentHeader";
import Modal from "components/shared/Modal";
import Survey from "components/shared/Survey";
import ReactDOM from "react-dom";
import { urlParams, validateParam } from "utils/paramsValidation";
import { Context } from "Context";
import Chat from "components/shared/Chat";
import appConfig from "config.json";
import { localeRegExp } from "utils/language";
import Seo from "components/shared/Seo";
import AriaLiveRegion from "components/shared/AriaLiveRegion";
import FloatingHelpHub from "components/floating-help-hub/FloatingHelpHub";

const headerComponent = "header";
const messageCenterComponent = "message-center";
const travelAgentHeaderComponent = "travel-agent-header";
const legalComponent = "legal";
const footerComponent = "footer";

const App = () => {
	const {
		config: {
			header,
			footer,
			legal,
			brand,
			hideOnPrint,
			oneIdOnly,
			skipOneId,
			environment,
		},
	} = useContext(Context);

	appConfig.skipOneId = skipOneId;

	const renderComponent = (targetContainer, component) => {
		let targetId = urlParams.get(`${targetContainer}Container`);

		if (!targetId && targetContainer === legalComponent) {
			targetId = urlParams.get(`${footerComponent}Container`);
		}

		const elementToRender = document.getElementById(
			targetId || `syndicated-${targetContainer}`
		);

		if (!elementToRender) return null;

		if (targetContainer === headerComponent) {
			elementToRender?.classList?.add("syndicated-header-parent");
			elementToRender.setAttribute("role", "banner");
		}

		if (appConfig.brands[brand]?.modifyZIndex) {
			elementToRender?.classList?.add("syndicated-header-parent-zindex");
		}

		if (hideOnPrint) {
			elementToRender?.classList?.add("syndicated-no-print");
		}

		return ReactDOM.createPortal(component, elementToRender);
	};

	const canRenderChat = () => {
		if (appConfig.brands[brand]?.externalScripts?.lpTagMessaging) {
			if (
				appConfig.brands[
					brand
				].externalScripts.lpTagMessaging.excludedEnvironments?.includes(
					environment
				)
			) {
				return false;
			}
			return true;
		} else {
			return false;
		}
	};

	const canRender = (configKey) => {
		if (appConfig[configKey].supportedBrands.includes(brand)) {
			if (appConfig[configKey].excludedEnvironments?.includes(environment)) {
				return false;
			}
			return true;
		} else {
			return false;
		}
	};

	useEffect(() => {
		const setLangAttribute = brand === "wdw" || brand === "dlr";
		if (setLangAttribute) {
			let lang = "en-us";
			const origin = window.location.origin;
			if (origin.endsWith(".eu")) {
				lang = "en-eu";
			} else if (origin.endsWith(".co.uk")) {
				lang = "en-gb";
			} else {
				const currentLocaleFromPath =
					window.location.pathname.match(localeRegExp)?.[0] || "";
				lang = currentLocaleFromPath
					? currentLocaleFromPath
							.toLowerCase()
							.replace(/\//g, "")
							.replace(/_/g, "-")
					: lang;
			}
			document.getElementsByTagName("html")[0].setAttribute("lang", lang);
		}
	}, [brand]);

	return (
		<div id="syndicated-root__app">
			{oneIdOnly ? (
				<></>
			) : (
				<>
					<AriaLiveRegion />
					{canRenderChat() && <Chat />}
					{validateParam(messageCenterComponent) &&
						renderComponent(messageCenterComponent, <MessageCenter />)}
					{validateParam(travelAgentHeaderComponent) &&
						renderComponent(
							travelAgentHeaderComponent,
							<TravelAgentHeader isolated />
						)}
					{header && renderComponent(headerComponent, <Header />)}
					{footer && renderComponent(footerComponent, <Footer />)}
					{legal && renderComponent(legalComponent, <Legal />)}
					<Modal />
					<Survey />
					{canRender("seo") && <Seo />}
					{canRender("floatingHelpHub") && <FloatingHelpHub />}
				</>
			)}
		</div>
	);
};

export default App;
